import type { AdminJob, JobFileData } from '@ecowheels/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button, Card, ConfirmButton, Input, PageHeader, QueryState } from '@/components/ui';

const DRIVE: Record<number, string> = { 1: 'Összkerék', 2: 'Két kerék', 3: 'Nem tudom' };
const GEARBOX: Record<number, string> = { 1: 'Manuális', 2: 'Automata' };

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString('hu-HU') : '—';

function JobFiles({ job }: { job: AdminJob }) {
  const [open, setOpen] = useState(false);

  // Files are fetched only when asked for: each one is a base64 blob of the
  // original photo, and the list endpoint deliberately returns names only.
  const { data, isLoading, error } = useQuery({
    queryKey: ['job-files', job.id],
    queryFn: () => api.post<JobFileData[]>('/admin/jobs/files', job.files.map((f) => f.id)),
    enabled: open && job.files.length > 0,
  });

  if (job.files.length === 0) {
    return <p className="text-xs text-white/40">Nincs feltöltött fájl.</p>;
  }

  return (
    <div>
      <Button variant="ghost" onClick={() => setOpen((v) => !v)}>
        {open ? 'Fájlok elrejtése' : `Fájlok megtekintése (${job.files.length})`}
      </Button>

      {open && (
        <div className="mt-3">
          {isLoading && <p className="text-xs text-white/40">Fájlok betöltése…</p>}
          {error && <p className="text-xs text-red-400">A fájlokat nem sikerült betölteni.</p>}
          <div className="flex flex-wrap gap-3">
            {data?.map((file) => {
              const src = `data:image/*;base64,${file.data}`;
              return (
                <figure key={file.id} className="w-48">
                  <a href={src} download={file.filename} title="Letöltés">
                    <img
                      src={src}
                      alt={file.filename}
                      className="h-32 w-48 rounded-md border border-white/10 object-cover"
                    />
                  </a>
                  <figcaption className="mt-1 truncate text-xs text-white/50">
                    {file.filename}
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function JobRow({
  job,
  onDelete,
  onRetry,
  retrying,
}: {
  job: AdminJob;
  onDelete: (id: number) => void;
  onRetry: (id: number) => void;
  retrying: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-medium">
            {job.nev_cegnev}
            {job.ceg && (
              <span className="ml-2 rounded bg-brand/15 px-1.5 py-0.5 text-xs text-brand">Cég</span>
            )}
          </p>
          <p className="mt-1 text-sm text-white/60">
            {job.marka} {job.model} · {formatDate(job.created_at)}
          </p>
          <p className="mt-1 text-sm text-white/60">
            <a href={`tel:${job.telefon}`} className="hover:text-brand">
              {job.telefon || job.ceg_kapcsolattarto_telefon}
            </a>
            {' · '}
            <a href={`mailto:${job.email}`} className="hover:text-brand">
              {job.email}
            </a>
          </p>
          <p className="mt-1 text-xs text-red-400">
            {job.notificationFailedAt
              ? `Szerviz-értesítés sikertelen (${formatDate(job.notificationFailedAt)})`
              : 'Szerviz-értesítés még nem ment ki'}
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Kevesebb' : 'Részletek'}
          </Button>
          <Button variant="ghost" onClick={() => onRetry(job.id)} disabled={retrying}>
            {retrying ? 'Küldés…' : 'Újraküldés'}
          </Button>
          <ConfirmButton
            onConfirm={() => onDelete(job.id)}
            confirmText={`Biztosan törli ${job.nev_cegnev} munkafelvételét? A feltöltött fájlok is törlődnek, a szerviz nem lesz róla értesítve. Ez nem vonható vissza.`}
          >
            Törlés
          </ConfirmButton>
        </div>
      </div>

      {open && (
        <dl className="mt-4 grid gap-x-6 gap-y-2 border-t border-white/10 pt-4 text-sm sm:grid-cols-2">
          {[
            ['Cím', `${job.irsz} ${job.telepules}, ${job.cim}`],
            ['Kategória', [job.mainType, job.type].filter(Boolean).join(' · ') || '—'],
            ['Meghajtás', job.osszkerek ? DRIVE[job.osszkerek] : '—'],
            ['Váltó', job.valto ? GEARBOX[job.valto] : '—'],
            ['Műszaki lejárat', formatDate(job.muszaki_lejarat)],
            ['Abroncs', job.abroncsmeret || '—'],
            ['Abroncs (hátul)', job.abroncsmeret_hatul || '—'],
            ...(job.ceg
              ? ([
                  ['Adószám', job.ceg_adoszam ?? '—'],
                  ['Kapcsolattartó', job.ceg_kapcsolattarto_nev ?? '—'],
                ] as [string, string][])
              : []),
          ].map(([label, value]) => (
            <div key={label} className="flex gap-2">
              <dt className="w-36 shrink-0 text-white/40">{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}

          {job.megjegyzes && (
            <div className="flex gap-2 sm:col-span-2">
              <dt className="w-36 shrink-0 text-white/40">Megjegyzés</dt>
              <dd className="whitespace-pre-wrap">{job.megjegyzes}</dd>
            </div>
          )}

          <div className="sm:col-span-2">
            <JobFiles job={job} />
          </div>
        </dl>
      )}
    </Card>
  );
}

export function JobsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api.get<AdminJob[]>('/admin/jobs'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/jobs/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  });

  const retry = useMutation({
    mutationFn: (id: number) => api.post<{ ok: boolean }>(`/admin/jobs/${id}/retry`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  });

  const jobs = useMemo(() => {
    const list = [...(data ?? [])].sort((a, b) => {
      // Oldest failure first: this list only ever holds submissions the
      // workshop hasn't been notified about, so it's a queue to clear, not a
      // feed of recent activity — the one stuck longest needs attention first.
      // A job that has never been attempted (no notificationFailedAt at all,
      // e.g. a crash between save and send) sorts by its submission time.
      const at = new Date(a.notificationFailedAt ?? a.created_at).getTime();
      const bt = new Date(b.notificationFailedAt ?? b.created_at).getTime();
      return at - bt;
    });
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((job) =>
      [job.nev_cegnev, job.email, job.telefon, job.marka, job.model, job.telepules]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [data, search]);

  return (
    <>
      <PageHeader
        title="Munkafelvételek"
        action={
          <Input
            type="search"
            placeholder="Keresés név, email, rendszám…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
        }
      />

      <p className="mb-4 text-xs text-white/40">
        Csak azok a munkafelvételek látszanak itt, amelyeknél a szerviz értesítése még nem
        sikerült — a sikeres értesítés után a bejegyzés és a feltöltött fájlok automatikusan
        törlődnek.
      </p>

      {remove.error && (
        <p className="mb-4 text-sm text-red-400">A törlés nem sikerült. Kérjük próbálja újra.</p>
      )}
      {retry.error && (
        <p className="mb-4 text-sm text-red-400">Az újraküldés nem sikerült. Kérjük próbálja újra.</p>
      )}

      <QueryState
        isLoading={isLoading}
        error={error}
        isEmpty={jobs.length === 0}
        emptyText={search ? 'Nincs találat.' : 'Nincs függőben lévő munkafelvétel.'}
      >
        <p className="mb-3 text-xs text-white/40">{jobs.length} találat</p>
        <div className="space-y-3">
          {jobs.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              onDelete={remove.mutate}
              onRetry={retry.mutate}
              retrying={retry.isPending && retry.variables === job.id}
            />
          ))}
        </div>
      </QueryState>
    </>
  );
}
